"use client";

import { useNavigate } from "react-router-dom";
import {
  IconFileImport,
  IconFileExport,
  IconTruck,
  IconShip,
  IconLock,
  IconSparkles,
  IconArrowRight,
  IconBuildingWarehouse,
  IconPackageExport,
  IconArrowUpRight,
  IconArrowDownLeft,
  IconArrowsExchange,
  IconPackage,
  IconLogin2,
  IconLogout2,
  IconCertificate,
} from "@tabler/icons-react";
import { useTheme } from "@contexts/ThemeContext";
import { useLanguage } from "@contexts/LanguageContext";
import { DARK_THEME_MODES } from "@constants/colors";
import { motion } from "framer-motion";

// Email-based edit access rules (only users with 'edit' can create new documents)
const BC20_EDIT_EMAILS = [
  "christina.vonny@molgroup.com",
  "ari.wibowo@molgroup.com",
  "femy.muthia@molgroup.com",
  "lanny.apriliany@molgroup.com",
  "mlg-id.seaimport01@molgroup.com",
  "mlg-id.airport01@molgroup.com",
];

const BC30_EDIT_EMAILS = [
  "christina.vonny@molgroup.com",
  "ari.wibowo@molgroup.com",
  "femy.muthia@molgroup.com",
  "reza.haqqa@molgroup.com",
  "ratri.puji@molgroup.com",
  "prama.adi@molgroup.com",
  "mlg-id.airexport01@molgroup.com",
];

const getUserEmail = (): string => {
  try {
    const raw = localStorage.getItem("authData");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return (parsed?.email || "").toLowerCase();
  } catch {
    return "";
  }
};

interface Product {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  available: boolean;
  link?: string;
  badge?: string;
  badgeVariant?: "solid" | "dashed";
  color: string;
  code: string;
  /** Document family labels shown on the card (e.g. BC 2.0 / BC 3.0) */
  docCodes?: string[];
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

export default function BuatAjuPage() {
  const navigate = useNavigate();
  const { mode, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const isDark = DARK_THEME_MODES.includes(mode);

  const userEmail = getUserEmail();
  const canEditImport = !userEmail.endsWith("@molgroup.com") || BC20_EDIT_EMAILS.includes(userEmail);
  const canEditExport = !userEmail.endsWith("@molgroup.com") || BC30_EDIT_EMAILS.includes(userEmail);

  const primaryProducts: Product[] = [
    {
      id: "import",
      title: t("buataju.import"),
      description: t("buataju.importDesc"),
      icon: <IconFileImport size={22} stroke={1.9} />,
      available: canEditImport,
      link: "/doc-pabean/upload?type=import",
      badge: canEditImport ? t("buataju.popular") : t("buataju.limitedAccess"),
      color: "#4F46E5",
      code: t("buataju.codeImport"),
      docCodes: ["BC 2.0"],
    },
    {
      id: "export",
      title: t("buataju.export"),
      description: t("buataju.exportDesc"),
      icon: <IconFileExport size={22} stroke={1.9} />,
      available: canEditExport,
      link: "/doc-pabean/upload?type=export",
      badge: canEditExport ? t("buataju.popular") : t("buataju.limitedAccess"),
      color: "#059669",
      code: t("buataju.codeExport"),
      docCodes: ["BC 3.0"],
    },
    {
      id: "bc33",
      title: "BC 3.3",
      description: t("buataju.bc33Desc"),
      icon: <IconPackageExport size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#0284C7",
      code: t("buataju.codeEkspor"),
      docCodes: ["BC 3.3"],
    },
    {
      id: "ppftz",
      title: "PPFTZ",
      description: t("buataju.ppftzDesc"),
      icon: <IconBuildingWarehouse size={22} stroke={1.9} />,
      available: true,
      link: "/doc-pabean/upload?type=ppftz",
      badge: t("buataju.new"),
      badgeVariant: "dashed",
      color: "#0D9488",
      code: t("buataju.codePpftz"),
    },
  ];

  // TPB products from Beacukai OpenAPI (3.TPB) — unique Schema/Payload pairs
  const tpbProducts: Product[] = [
    {
      id: "bc16",
      title: "BC 1.6",
      description: t("buataju.bc16Desc"),
      icon: <IconShip size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#8B5CF6",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 1.6"],
    },
    {
      id: "bc23",
      title: "BC 2.3",
      description: t("buataju.bc23Desc"),
      icon: <IconTruck size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#F59E0B",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 2.3"],
    },
    {
      id: "bc25",
      title: "BC 2.5",
      description: t("buataju.bc25Desc"),
      icon: <IconPackageExport size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#EA580C",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 2.5"],
    },
    {
      id: "bc261",
      title: "BC 2.6.1",
      description: t("buataju.bc261Desc"),
      icon: <IconArrowUpRight size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#DB2777",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 2.6.1"],
    },
    {
      id: "bc262",
      title: "BC 2.6.2",
      description: t("buataju.bc262Desc"),
      icon: <IconArrowDownLeft size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#BE185D",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 2.6.2"],
    },
    {
      id: "bc27",
      title: "BC 2.7",
      description: t("buataju.bc27Desc"),
      icon: <IconArrowsExchange size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#7C3AED",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 2.7"],
    },
    {
      id: "bc28",
      title: "BC 2.8",
      description: t("buataju.bc28Desc"),
      icon: <IconPackage size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#2563EB",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 2.8"],
    },
    {
      id: "bc40",
      title: "BC 4.0",
      description: t("buataju.bc40Desc"),
      icon: <IconLogin2 size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#0F766E",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 4.0"],
    },
    {
      id: "bc41",
      title: "BC 4.1",
      description: t("buataju.bc41Desc"),
      icon: <IconLogout2 size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#047857",
      code: t("buataju.codeTpb"),
      docCodes: ["BC 4.1"],
    },
    {
      id: "bcp3bet",
      title: "BC P3BET",
      description: t("buataju.bcp3betDesc"),
      icon: <IconCertificate size={22} stroke={1.9} />,
      available: false,
      badge: t("buataju.comingSoon"),
      color: "#9333EA",
      code: t("buataju.codeTpb"),
      docCodes: ["BC P3BET"],
    },
  ];

  const products = [...primaryProducts, ...tpbProducts];

  const handleSelect = (p: Product) => {
    if (!p.available || !p.link) return;
    navigate(p.link);
  };

  const mutedIconBg = isDark ? themeColors.formFields : "#F5F6F8";
  const mutedIconColor = themeColors.placeholder ?? themeColors.textSecondary;
  const footerBorder = isDark ? themeColors.border : "#F1F3F5";
  const availableCount = products.length;

  const renderCard = (p: Product) => {
    const disabled = !p.available || !p.link;
    const accent = p.color;
    const iconBg = disabled ? mutedIconBg : `${accent}18`;
    const iconColor = disabled ? mutedIconColor : accent;
    const titleColor = disabled ? themeColors.textSecondary : themeColors.text;
    const descColor = disabled ? mutedIconColor : themeColors.textSecondary;
    const codeColor = disabled ? (isDark ? themeColors.placeholder : "#B6BCC6") : mutedIconColor;
    const actionColor = disabled ? (isDark ? themeColors.placeholder : "#B6BCC6") : accent;

    return (
      <motion.div
        key={p.id}
        data-tour={`buat-aju-${p.id}`}
        variants={item}
        whileHover={!disabled ? { y: -3 } : undefined}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => handleSelect(p)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect(p);
          }
        }}
        className={[
          "group relative flex min-h-[270px] flex-col rounded-2xl p-[22px] transition-all duration-200 ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        ].join(" ")}
        style={{
          backgroundColor: disabled
            ? isDark
              ? themeColors.formFields
              : "#FBFBFC"
            : themeColors.card,
          border: `1px solid ${themeColors.border}`,
          boxShadow: disabled
            ? "0 1px 2px rgba(16,24,40,0.03)"
            : "0 1px 2px rgba(16,24,40,0.04)",
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          const el = e.currentTarget;
          el.style.boxShadow = "0 12px 28px -8px rgba(16,24,40,0.14)";
          el.style.borderColor = isDark ? themeColors.textSecondary : "#D7DAE0";
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          const el = e.currentTarget;
          el.style.boxShadow = "0 1px 2px rgba(16,24,40,0.04)";
          el.style.borderColor = themeColors.border;
        }}
      >
        {/* Top: icon + badge */}
        <div className="flex items-start justify-between gap-2.5">
          <div
            className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: iconBg, color: iconColor }}
          >
            {p.icon}
          </div>

          {p.badge && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-[9px] py-1 text-[11px] font-semibold leading-none"
              style={
                disabled
                  ? {
                      backgroundColor: isDark ? themeColors.border : "#F1F3F5",
                      color: themeColors.textSecondary,
                    }
                  : p.badgeVariant === "dashed"
                    ? {
                        backgroundColor: themeColors.card,
                        color: accent,
                        border: `1px dashed ${accent}99`,
                      }
                    : {
                        backgroundColor: `${accent}18`,
                        color: accent,
                      }
              }
            >
              {disabled && <IconLock size={11} stroke={2.2} />}
              {!disabled && p.badge === t("buataju.popular") && <IconSparkles size={11} stroke={2.2} />}
              {p.badge}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="mt-5">
          <h3
            className="mb-[7px] text-[17px] font-bold tracking-tight"
            style={{ color: titleColor }}
          >
            {p.title}
          </h3>
          <p className="m-0 text-[13.5px] leading-[1.55]" style={{ color: descColor }}>
            {p.description}
          </p>
          {p.docCodes && p.docCodes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {p.docCodes.map((docCode) => (
                <span
                  key={docCode}
                  className="inline-flex items-center rounded-md px-2 py-[3px] text-[11px] font-semibold tracking-wide"
                  style={
                    disabled
                      ? {
                          backgroundColor: isDark ? themeColors.border : "#F1F3F5",
                          color: themeColors.textSecondary,
                        }
                      : {
                          backgroundColor: `${accent}14`,
                          color: accent,
                          border: `1px solid ${accent}33`,
                        }
                  }
                >
                  {docCode}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="mt-auto flex items-center justify-between border-t pt-[22px]"
          style={{ borderColor: footerBorder }}
        >
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.06em]"
            style={{ color: codeColor }}
          >
            {p.code}
          </span>
          <span
            className="inline-flex items-center gap-[5px] text-[13px] font-bold transition-all duration-200 group-hover:gap-2"
            style={{ color: actionColor }}
          >
            {disabled ? (
              t("buataju.locked")
            ) : (
              <>
                {t("buataju.start")}
                <IconArrowRight size={15} stroke={2.2} className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </span>
        </div>
      </motion.div>
    );
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] px-6 py-10 md:px-12 md:py-12"
      style={{ backgroundColor: themeColors.background }}
    >
      <div className="mx-auto max-w-[1240px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 flex flex-col gap-6 md:mb-8 md:flex-row md:items-end md:justify-between"
        >
          <div className="max-w-[620px]">
            <div
              className="mb-[18px] inline-flex items-center gap-[7px] rounded-full px-[11px] py-[5px] pl-[9px] text-xs font-semibold"
              style={{
                backgroundColor: themeColors.card,
                border: `1px solid ${themeColors.border}`,
                color: themeColors.textSecondary,
              }}
            >
              <IconSparkles size={14} stroke={2} style={{ color: themeColors.primary }} />
              {t("buataju.badge")}
            </div>

            <h1
              className="mb-2.5 text-[28px] font-extrabold leading-[1.1] tracking-tight md:text-[34px]"
              style={{ color: themeColors.text }}
            >
              {t("buataju.title")}
            </h1>
            <p className="m-0 text-[15px] font-normal leading-[1.6]" style={{ color: themeColors.textSecondary }}>
              {t("buataju.subtitle")}
            </p>
          </div>

          <div
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-[10px] border px-3.5 py-2 text-[13px] font-semibold"
            style={{
              backgroundColor: themeColors.card,
              borderColor: themeColors.border,
              color: themeColors.text,
            }}
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{
                backgroundColor: themeColors.success ?? "#16A34A",
                boxShadow: `0 0 0 3px ${(themeColors.success ?? "#16A34A")}26`,
              }}
            />
            {availableCount} {t("buataju.typesAvailable")}
          </div>
        </motion.div>

        {/* Primary cards */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          data-tour="buat-aju-grid"
          className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {primaryProducts.map(renderCard)}
        </motion.div>

        {/* TPB section */}
        <div className="mt-10">
          <div className="mb-4 flex items-center gap-3">
            <h2
              className="m-0 text-[15px] font-bold tracking-tight"
              style={{ color: themeColors.text }}
            >
              {t("buataju.tpb")}
            </h2>
            <span
              className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
              style={{
                backgroundColor: isDark ? themeColors.border : "#F1F3F5",
                color: themeColors.textSecondary,
              }}
            >
              {t("buataju.tpbDesc")}
            </span>
            <div
              className="h-px flex-1"
              style={{ backgroundColor: themeColors.border }}
            />
          </div>

          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {tpbProducts.map(renderCard)}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
